import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { businessCalendar } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCivilDate } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

/** "09:00" a partir dos minutos desde a meia-noite. */
function horaDosMinutos(minutos: number): string {
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return `${String(horas).padStart(2, '0')}:${String(resto).padStart(2, '0')}`;
}

/** Minutos desde a meia-noite a partir de "09:00"; null se o texto nao e hora. */
function minutosDaHora(texto: string): number | null {
  const partes = /^(\d{2}):(\d{2})$/.exec(texto.trim());
  if (!partes) return null;
  const horas = Number(partes[1]);
  const minutos = Number(partes[2]);
  if (horas > 24 || minutos > 59 || (horas === 24 && minutos > 0)) return null;
  return horas * 60 + minutos;
}

/**
 * Aba Feriados das configuracoes (P-BI-1): o horario comercial e os feriados da
 * empresa.
 *
 * O prazo das esteiras no OnPoint conta so o horario comercial, de segunda a sexta,
 * das 9h as 18h (Instrucao SLA). A regra de apuracao ainda nao foi homologada: o
 * calendario fica registrado aqui, e o calculo em dias uteis o le quando a regra
 * chegar. As permissoes sao as do prazo por etapa.
 */
export function BusinessCalendarPanel() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeVer = can('configuracoes.sla.view');
  const podeEditar = can('configuracoes.sla.update');

  const horario = useQuery({
    queryKey: ['business-hours'],
    queryFn: ({ signal }) => businessCalendar.hours(signal),
    enabled: podeVer,
  });
  const feriados = useQuery({
    queryKey: ['business-holidays'],
    queryFn: ({ signal }) => businessCalendar.holidays(undefined, signal),
    enabled: podeVer,
  });

  const [inicio, setInicio] = useState<string | null>(null);
  const [fim, setFim] = useState<string | null>(null);
  const [data, setData] = useState('');
  const [nome, setNome] = useState('');

  const inicioTexto =
    inicio ?? (horario.data ? horaDosMinutos(horario.data.start_minute) : '');
  const fimTexto = fim ?? (horario.data ? horaDosMinutos(horario.data.end_minute) : '');
  const inicioMinutos = minutosDaHora(inicioTexto);
  const fimMinutos = minutosDaHora(fimTexto);
  const horarioInvalido =
    inicioMinutos === null || fimMinutos === null || fimMinutos <= inicioMinutos;

  const salvarHorario = useMutation({
    mutationFn: () =>
      businessCalendar.setHours({
        start_minute: inicioMinutos ?? 0,
        end_minute: fimMinutos ?? 0,
      }),
    onSuccess: () => {
      toast.success('Horario comercial gravado.');
      setInicio(null);
      setFim(null);
      void queryClient.invalidateQueries({ queryKey: ['business-hours'] });
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const criar = useMutation({
    mutationFn: () => businessCalendar.createHoliday({ date: data, name: nome }),
    onSuccess: () => {
      toast.success('Feriado cadastrado.');
      setData('');
      setNome('');
      void queryClient.invalidateQueries({ queryKey: ['business-holidays'] });
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => businessCalendar.removeHoliday(id),
    onSuccess: () => {
      toast.success('Feriado excluido.');
      void queryClient.invalidateQueries({ queryKey: ['business-holidays'] });
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  if (!podeVer) return null;

  function cadastrar(evento: FormEvent) {
    evento.preventDefault();
    criar.mutate();
  }

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader
          title="Horario comercial"
          description="Horario em que o prazo das esteiras corre, de segunda a sexta."
        />
        <CardBody className="flex flex-col gap-3">
          <Alert tone="info">
            Nasce com o horario do sistema anterior (9h as 18h). A regra de apuracao do
            prazo em dias uteis ainda nao foi homologada pela seguradora.
          </Alert>
          {horario.isPending ? (
            <Skeleton className="h-20 w-full" />
          ) : (
            <form
              className="grid gap-3 sm:grid-cols-2"
              onSubmit={(evento) => {
                evento.preventDefault();
                salvarHorario.mutate();
              }}
            >
              <Field
                label="Inicio do horario comercial"
                type="time"
                value={inicioTexto}
                disabled={!podeEditar}
                onChange={(evento) => setInicio(evento.target.value)}
              />
              <Field
                label="Fim do horario comercial"
                type="time"
                value={fimTexto}
                disabled={!podeEditar}
                error={
                  horarioInvalido && (inicio !== null || fim !== null)
                    ? 'O fim precisa ser depois do inicio.'
                    : undefined
                }
                onChange={(evento) => setFim(evento.target.value)}
              />
              {podeEditar ? (
                <div className="sm:col-span-2">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={horarioInvalido || salvarHorario.isPending}
                  >
                    Salvar horario
                  </Button>
                </div>
              ) : null}
            </form>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Feriados"
          description="Dias que nao contam no prazo das esteiras."
        />
        <CardBody className="flex flex-col gap-3">
          {podeEditar ? (
            <form
              className="grid gap-3 sm:grid-cols-[10rem_1fr_auto]"
              onSubmit={cadastrar}
            >
              <Field
                label="Data do feriado"
                type="date"
                value={data}
                required
                onChange={(evento) => setData(evento.target.value)}
              />
              <Field
                label="Nome do feriado"
                value={nome}
                maxLength={120}
                required
                onChange={(evento) => setNome(evento.target.value)}
              />
              <div className="flex items-end">
                <Button
                  type="submit"
                  size="sm"
                  disabled={!data || !nome.trim() || criar.isPending}
                >
                  Adicionar
                </Button>
              </div>
            </form>
          ) : null}
          {feriados.isError ? (
            <Alert tone="danger" title="Nao foi possivel carregar os feriados">
              {feriados.error.message}
            </Alert>
          ) : feriados.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : feriados.data.length === 0 ? (
            <p className="text-sm text-ink-500">Nenhum feriado cadastrado.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
              {feriados.data.map((feriado) => (
                <li
                  key={feriado.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span>
                    <span className="font-medium tabular-nums text-ink-900">
                      {formatCivilDate(feriado.date)}
                    </span>{' '}
                    <span className="text-ink-600">{feriado.name}</span>
                  </span>
                  {podeEditar ? (
                    <AcaoComIcone
                      acao="excluir"
                      rotulo="Excluir"
                      aria-label={`Excluir o feriado ${feriado.name}`}
                      disabled={excluir.isPending}
                      onClick={() => excluir.mutate(feriado.id)}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
